import type { AxiosRequestConfig } from "axios"
import z from "zod"
import type {
  BatchCreateBotResponseDataItem,
  BatchCreateBotResponseErrorsItem,
  BatchCreateBotsRequestBodyInput,
  BatchCreateScheduledBotResponseDataItem,
  BatchCreateScheduledBotResponseErrorsItem,
  BatchCreateScheduledBotsRequestBodyInput,
  CreateBotRequestBodyInput,
  CreateBotResponseData,
  CreateCalendarBotRequestBodyInput,
  CreateCalendarBotResponseDataItem,
  CreateCalendarBotResponseErrorsItem,
  CreateCalendarConnectionRequestBodyInput,
  CreateCalendarConnectionResponseData,
  CreateMeetLogin201Data,
  CreateMeetLoginBody,
  CreateMeetWorkspace201Data,
  CreateMeetWorkspaceBody,
  CreateScheduledBotRequestBodyInput,
  CreateScheduledBotResponseData,
  CreateZoomCredential201Data,
  CreateZoomCredentialBody,
  DeleteBotDataResponseData,
  DeleteCalendarBotBody,
  DeleteCalendarBotResponseDataItem,
  DeleteCalendarBotResponseErrorsItem,
  DeleteCalendarConnectionResponseData,
  DeleteMeetLogin200Data,
  DeleteMeetWorkspace200Data,
  DeleteScheduledBotResponseData,
  DeleteZoomCredential200Data,
  GetBotDetailsResponseData,
  GetBotScreenshotsResponseDataItem,
  GetBotStatusResponseData,
  GetCalendarDetailsResponseData,
  GetEventDetailsResponseData,
  GetMeetLogin200Data,
  GetMeetLoginUtilization200Data,
  GetMeetWorkspace200Data,
  GetScheduledBotResponseData,
  GetZoomCredential200Data,
  LeaveBotResponseData,
  ListBotsParams,
  ListBotsResponseDataItem,
  ListCalendarsParams,
  ListCalendarsResponseDataItem,
  ListEventSeriesParams,
  ListEventSeriesResponseDataItem,
  ListEventsParams as ListEventsParamsV2,
  ListEventsResponseDataItem,
  ListMeetLogins200DataItem,
  ListMeetLoginsParams,
  ListMeetWorkspaces200DataItem,
  ListMeetWorkspacesParams,
  ListRawCalendarsRequestBodyInput,
  ListRawCalendarsResponseDataItem,
  ListScheduledBotsParams,
  ListScheduledBotsResponseDataItem,
  ListZoomCredentials200DataItem,
  ListZoomCredentialsParams,
  PauseBotRecording200Data,
  PauseBotRecordingBody,
  ResendFinalWebhookResponseData,
  ResubscribeCalendarResponseData,
  ResumeBotRecording200Data,
  ResumeBotRecordingBody,
  RetranscribeBot200Data,
  RetranscribeBotBody,
  RetryCallbackRequestBodyInput,
  RetryCallbackResponseData,
  SendChatMessage200Data,
  SendChatMessageBody,
  SyncCalendarResponseData,
  UpdateBotConfig200Data,
  UpdateBotConfigBody,
  UpdateCalendarBotRequestBodyInput,
  UpdateCalendarBotResponseDataItem,
  UpdateCalendarBotResponseErrorsItem,
  UpdateCalendarConnectionRequestBodyInput,
  UpdateCalendarConnectionResponseData,
  UpdateMeetLogin200Data,
  UpdateMeetLoginBody,
  UpdateMeetWorkspace200Data,
  UpdateMeetWorkspaceBody,
  UpdateScheduledBotRequestBodyInput,
  UpdateScheduledBotResponseData,
  UpdateZoomCredential200Data,
  UpdateZoomCredentialBody
} from "../generated/v2/schema"
import {
  type ApiResponseV2,
  apiWrapperV2,
  apiWrapperV2List,
  apiWrapperV2NoParams,
  type BatchApiResponseV2,
  type ListApiResponseV2
} from "./api"
import type { ClientState } from "./client-state"
import type { BaasClientV2Methods } from "./types"

/**
 * Create v2 client methods
 * Methods use pass-through responses (ApiResponseV2<T>)
 */
export function createV2Methods(state: ClientState): BaasClientV2Methods {
  return {
    // Bot methods
    async createBot(
      params: CreateBotRequestBodyInput
    ): Promise<ApiResponseV2<CreateBotResponseData>> {
      const { createBot: createBotApi } = await import("../generated/v2/api/bots/bots.js")
      const { createBotBody } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<CreateBotResponseData, CreateBotRequestBodyInput>(
        createBotApi,
        createBotBody as z.ZodType<CreateBotRequestBodyInput>,
        params,
        state.getOptions()
      )
    },

    async batchCreateBots(
      params: BatchCreateBotsRequestBodyInput
    ): Promise<
      BatchApiResponseV2<BatchCreateBotResponseDataItem, BatchCreateBotResponseErrorsItem>
    > {
      const { batchCreateBots: batchCreateBotsApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { batchCreateBotsBody } = await import("../generated/v2/api/bots/bots.zod.js")

      // Note: batchCreateBots returns BatchApiResponseV2, but apiWrapperV2 returns ApiResponseV2
      // We need to handle this specially or update apiWrapperV2 to support batch responses
      return apiWrapperV2<BatchCreateBotResponseDataItem[], BatchCreateBotsRequestBodyInput>(
        batchCreateBotsApi,
        batchCreateBotsBody as z.ZodType<BatchCreateBotsRequestBodyInput>,
        params,
        state.getOptions()
      ) as Promise<
        BatchApiResponseV2<BatchCreateBotResponseDataItem, BatchCreateBotResponseErrorsItem>
      >
    },

    async listBots(params?: ListBotsParams): Promise<ListApiResponseV2<ListBotsResponseDataItem>> {
      const { listBots: listBotsApi } = await import("../generated/v2/api/bots/bots.js")
      const { listBotsQueryParams } = await import("../generated/v2/api/bots/bots.zod.js")

      // Provide default limit if params is undefined or limit is not provided
      const paramsWithDefaults: ListBotsParams = params ? { limit: 50, ...params } : { limit: 50 }

      return apiWrapperV2List<ListBotsResponseDataItem, ListBotsParams>(
        listBotsApi,
        listBotsQueryParams,
        paramsWithDefaults,
        state.getOptions()
      )
    },

    async getBotDetails(params: {
      bot_id: string
    }): Promise<ApiResponseV2<GetBotDetailsResponseData>> {
      const { getBotDetails: getBotDetailsApi } = await import("../generated/v2/api/bots/bots.js")
      const { getBotDetailsParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<GetBotDetailsResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          getBotDetailsApi(params.bot_id, options),
        getBotDetailsParams,
        params,
        state.getOptions()
      )
    },

    async getBotStatus(params: {
      bot_id: string
    }): Promise<ApiResponseV2<GetBotStatusResponseData>> {
      const { getBotStatus: getBotStatusApi } = await import("../generated/v2/api/bots/bots.js")
      const { getBotStatusParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<GetBotStatusResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          getBotStatusApi(params.bot_id, options),
        getBotStatusParams,
        params,
        state.getOptions()
      )
    },

    async getBotScreenshots(params: {
      bot_id: string
      limit?: number
      cursor?: string | null
    }): Promise<ListApiResponseV2<GetBotScreenshotsResponseDataItem>> {
      const { getBotScreenshots: getBotScreenshotsApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { getBotScreenshotsParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2List<
        GetBotScreenshotsResponseDataItem,
        { bot_id: string; limit?: number; cursor?: string | null }
      >(
        (params: { bot_id: string; limit?: number; cursor?: string | null }, options) =>
          getBotScreenshotsApi(
            params.bot_id,
            { limit: params.limit, cursor: params.cursor },
            options
          ),
        z.object({
          bot_id: getBotScreenshotsParams.shape.bot_id,
          limit: z.number().optional(),
          cursor: z.string().nullable().optional()
        }),
        params,
        state.getOptions()
      )
    },

    async leaveBot(params: { bot_id: string }): Promise<ApiResponseV2<LeaveBotResponseData>> {
      const { leaveBot: leaveBotApi } = await import("../generated/v2/api/bots/bots.js")
      const { leaveBotParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<LeaveBotResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          leaveBotApi(params.bot_id, options),
        leaveBotParams,
        params,
        state.getOptions()
      )
    },

    async deleteBotData(params: {
      bot_id: string
      delete_from_provider?: boolean
    }): Promise<ApiResponseV2<DeleteBotDataResponseData>> {
      const { deleteBotData: deleteBotDataApi } = await import("../generated/v2/api/bots/bots.js")
      const { deleteBotDataParams, deleteBotDataQueryParams } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<
        DeleteBotDataResponseData,
        { bot_id: string; delete_from_provider?: boolean }
      >(
        (params: { bot_id: string; delete_from_provider?: boolean }, options) =>
          deleteBotDataApi(
            params.bot_id,
            { delete_from_provider: params.delete_from_provider },
            options
          ),
        z.object({
          bot_id: deleteBotDataParams.shape.bot_id,
          delete_from_provider: deleteBotDataQueryParams.shape.delete_from_provider.optional()
        }),
        params,
        state.getOptions()
      )
    },

    async resendFinalWebhook(params: {
      bot_id: string
    }): Promise<ApiResponseV2<ResendFinalWebhookResponseData>> {
      const { resendFinalWebhook: resendFinalWebhookApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { resendFinalWebhookParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<ResendFinalWebhookResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          resendFinalWebhookApi(params.bot_id, options),
        resendFinalWebhookParams,
        params,
        state.getOptions()
      )
    },

    async retryCallback(params: {
      bot_id: string
      callbackConfig?: RetryCallbackRequestBodyInput
    }): Promise<ApiResponseV2<RetryCallbackResponseData>> {
      const { retryCallback: retryCallbackApi } = await import("../generated/v2/api/bots/bots.js")
      const { retryCallbackParams, retryCallbackBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<
        RetryCallbackResponseData,
        { bot_id: string; callbackConfig?: RetryCallbackRequestBodyInput }
      >(
        (params: { bot_id: string; callbackConfig?: RetryCallbackRequestBodyInput }, options) =>
          retryCallbackApi(params.bot_id, params.callbackConfig ?? null, options),
        z.object({
          bot_id: retryCallbackParams.shape.bot_id,
          callbackConfig: retryCallbackBody.optional()
        }),
        params,
        state.getOptions()
      )
    },

    async retranscribeBot(params: {
      bot_id: string
      body?: RetranscribeBotBody
    }): Promise<ApiResponseV2<RetranscribeBot200Data>> {
      const { retranscribeBot: retranscribeBotApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { retranscribeBotParams, retranscribeBotBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<RetranscribeBot200Data, { bot_id: string; body?: RetranscribeBotBody }>(
        (params: { bot_id: string; body?: RetranscribeBotBody }, options) =>
          retranscribeBotApi(params.bot_id, params.body ?? null, options),
        z.object({
          bot_id: retranscribeBotParams.shape.bot_id,
          body: retranscribeBotBody.optional()
        }) as z.ZodType<{ bot_id: string; body?: RetranscribeBotBody }>,
        params,
        state.getOptions()
      )
    },

    async updateBotConfig(params: {
      bot_id: string
      body: UpdateBotConfigBody
    }): Promise<ApiResponseV2<UpdateBotConfig200Data>> {
      const { updateBotConfig: updateBotConfigApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { updateBotConfigParams, updateBotConfigBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<UpdateBotConfig200Data, { bot_id: string; body: UpdateBotConfigBody }>(
        (params: { bot_id: string; body: UpdateBotConfigBody }, options) =>
          updateBotConfigApi(params.bot_id, params.body, options),
        z.object({
          bot_id: updateBotConfigParams.shape.bot_id,
          body: updateBotConfigBody
        }),
        params,
        state.getOptions()
      )
    },

    async sendChatMessage(params: {
      bot_id: string
      body: SendChatMessageBody
    }): Promise<ApiResponseV2<SendChatMessage200Data>> {
      const { sendChatMessage: sendChatMessageApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { sendChatMessageParams, sendChatMessageBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<SendChatMessage200Data, { bot_id: string; body: SendChatMessageBody }>(
        (params: { bot_id: string; body: SendChatMessageBody }, options) =>
          sendChatMessageApi(params.bot_id, params.body, options),
        z.object({
          bot_id: sendChatMessageParams.shape.bot_id,
          body: sendChatMessageBody
        }),
        params,
        state.getOptions()
      )
    },

    async pauseBotRecording(params: {
      bot_id: string
      body?: PauseBotRecordingBody
    }): Promise<ApiResponseV2<PauseBotRecording200Data>> {
      const { pauseBotRecording: pauseBotRecordingApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { pauseBotRecordingParams, pauseBotRecordingBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<
        PauseBotRecording200Data,
        { bot_id: string; body?: PauseBotRecordingBody }
      >(
        (params: { bot_id: string; body?: PauseBotRecordingBody }, options) =>
          pauseBotRecordingApi(params.bot_id, params.body ?? {}, options),
        z.object({
          bot_id: pauseBotRecordingParams.shape.bot_id,
          body: pauseBotRecordingBody.optional()
        }),
        params,
        state.getOptions()
      )
    },

    async resumeBotRecording(params: {
      bot_id: string
      body?: ResumeBotRecordingBody
    }): Promise<ApiResponseV2<ResumeBotRecording200Data>> {
      const { resumeBotRecording: resumeBotRecordingApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { resumeBotRecordingParams, resumeBotRecordingBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<
        ResumeBotRecording200Data,
        { bot_id: string; body?: ResumeBotRecordingBody }
      >(
        (params: { bot_id: string; body?: ResumeBotRecordingBody }, options) =>
          resumeBotRecordingApi(params.bot_id, params.body ?? {}, options),
        z.object({
          bot_id: resumeBotRecordingParams.shape.bot_id,
          body: resumeBotRecordingBody.optional()
        }),
        params,
        state.getOptions()
      )
    },

    // Scheduled bot methods
    async createScheduledBot(
      params: CreateScheduledBotRequestBodyInput
    ): Promise<ApiResponseV2<CreateScheduledBotResponseData>> {
      const { createScheduledBot: createScheduledBotApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { createScheduledBotBody } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<CreateScheduledBotResponseData, CreateScheduledBotRequestBodyInput>(
        createScheduledBotApi,
        createScheduledBotBody as z.ZodType<CreateScheduledBotRequestBodyInput>,
        params,
        state.getOptions()
      )
    },

    async batchCreateScheduledBots(
      params: BatchCreateScheduledBotsRequestBodyInput
    ): Promise<
      BatchApiResponseV2<
        BatchCreateScheduledBotResponseDataItem,
        BatchCreateScheduledBotResponseErrorsItem
      >
    > {
      const { batchCreateScheduledBots: batchCreateScheduledBotsApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { batchCreateScheduledBotsBody } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<
        BatchCreateScheduledBotResponseDataItem[],
        BatchCreateScheduledBotsRequestBodyInput
      >(
        batchCreateScheduledBotsApi,
        batchCreateScheduledBotsBody as z.ZodType<BatchCreateScheduledBotsRequestBodyInput>,
        params,
        state.getOptions()
      ) as Promise<
        BatchApiResponseV2<
          BatchCreateScheduledBotResponseDataItem,
          BatchCreateScheduledBotResponseErrorsItem
        >
      >
    },

    async listScheduledBots(
      params?: ListScheduledBotsParams
    ): Promise<ListApiResponseV2<ListScheduledBotsResponseDataItem>> {
      const { listScheduledBots: listScheduledBotsApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { listScheduledBotsQueryParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2List<ListScheduledBotsResponseDataItem, ListScheduledBotsParams>(
        listScheduledBotsApi,
        listScheduledBotsQueryParams,
        params ?? { limit: 50 },
        state.getOptions()
      )
    },

    async getScheduledBot(params: {
      bot_id: string
    }): Promise<ApiResponseV2<GetScheduledBotResponseData>> {
      const { getScheduledBotDetails: getScheduledBotApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { getScheduledBotDetailsParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<GetScheduledBotResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          getScheduledBotApi(params.bot_id, options),
        getScheduledBotDetailsParams,
        params,
        state.getOptions()
      )
    },

    async updateScheduledBot(params: {
      bot_id: string
      body: UpdateScheduledBotRequestBodyInput
    }): Promise<ApiResponseV2<UpdateScheduledBotResponseData>> {
      const { updateScheduledBot: updateScheduledBotApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { updateScheduledBotParams, updateScheduledBotBody } = await import(
        "../generated/v2/api/bots/bots.zod.js"
      )

      return apiWrapperV2<
        UpdateScheduledBotResponseData,
        { bot_id: string; body: UpdateScheduledBotRequestBodyInput }
      >(
        (params: { bot_id: string; body: UpdateScheduledBotRequestBodyInput }, options) =>
          updateScheduledBotApi(params.bot_id, params.body, options),
        z.object({
          bot_id: updateScheduledBotParams.shape.bot_id,
          body: updateScheduledBotBody
        }) as z.ZodType<{ bot_id: string; body: UpdateScheduledBotRequestBodyInput }>,
        params,
        state.getOptions()
      )
    },

    async deleteScheduledBot(params: {
      bot_id: string
    }): Promise<ApiResponseV2<DeleteScheduledBotResponseData>> {
      const { deleteScheduledBot: deleteScheduledBotApi } = await import(
        "../generated/v2/api/bots/bots.js"
      )
      const { deleteScheduledBotParams } = await import("../generated/v2/api/bots/bots.zod.js")

      return apiWrapperV2<DeleteScheduledBotResponseData, { bot_id: string }>(
        (params: { bot_id: string }, options: AxiosRequestConfig) =>
          deleteScheduledBotApi(params.bot_id, options),
        deleteScheduledBotParams,
        params,
        state.getOptions()
      )
    },

    // Calendar methods
    async listRawCalendars(
      params: ListRawCalendarsRequestBodyInput
    ): Promise<ApiResponseV2<ListRawCalendarsResponseDataItem[]>> {
      const { listRawCalendars: listRawCalendarsApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { listRawCalendarsBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<ListRawCalendarsResponseDataItem[], ListRawCalendarsRequestBodyInput>(
        listRawCalendarsApi,
        listRawCalendarsBody,
        params,
        state.getOptions()
      )
    },

    async createCalendarConnection(
      params: CreateCalendarConnectionRequestBodyInput
    ): Promise<ApiResponseV2<CreateCalendarConnectionResponseData>> {
      const { createCalendarConnection: createCalendarConnectionApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { createCalendarConnectionBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<
        CreateCalendarConnectionResponseData,
        CreateCalendarConnectionRequestBodyInput
      >(createCalendarConnectionApi, createCalendarConnectionBody, params, state.getOptions())
    },

    async listCalendars(
      params?: ListCalendarsParams
    ): Promise<ListApiResponseV2<ListCalendarsResponseDataItem>> {
      const { listCalendars: listCalendarsApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { listCalendarsQueryParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      // Provide default limit if params is undefined or limit is not provided
      const paramsWithDefaults: ListCalendarsParams = params
        ? { limit: 50, ...params }
        : { limit: 50 }

      return apiWrapperV2List<ListCalendarsResponseDataItem, ListCalendarsParams>(
        listCalendarsApi,
        listCalendarsQueryParams,
        paramsWithDefaults,
        state.getOptions()
      )
    },

    async getCalendarDetails(params: {
      calendar_id: string
    }): Promise<ApiResponseV2<GetCalendarDetailsResponseData>> {
      const { getCalendarDetails: getCalendarDetailsApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { getCalendarDetailsParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<GetCalendarDetailsResponseData, { calendar_id: string }>(
        (params: { calendar_id: string }, options: AxiosRequestConfig) =>
          getCalendarDetailsApi(params.calendar_id, options),
        getCalendarDetailsParams,
        params,
        state.getOptions()
      )
    },

    async updateCalendarConnection(params: {
      calendar_id: string
      body: UpdateCalendarConnectionRequestBodyInput
    }): Promise<ApiResponseV2<UpdateCalendarConnectionResponseData>> {
      const { updateCalendarConnection: updateCalendarConnectionApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { updateCalendarConnectionParams, updateCalendarConnectionBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<
        UpdateCalendarConnectionResponseData,
        { calendar_id: string; body: UpdateCalendarConnectionRequestBodyInput }
      >(
        (
          params: { calendar_id: string; body: UpdateCalendarConnectionRequestBodyInput },
          options
        ) => updateCalendarConnectionApi(params.calendar_id, params.body, options),
        z.object({
          calendar_id: updateCalendarConnectionParams.shape.calendar_id,
          body: updateCalendarConnectionBody
        }),
        params,
        state.getOptions()
      )
    },

    async deleteCalendarConnection(params: {
      calendar_id: string
    }): Promise<ApiResponseV2<DeleteCalendarConnectionResponseData>> {
      const { deleteCalendarConnection: deleteCalendarConnectionApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { deleteCalendarConnectionParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<DeleteCalendarConnectionResponseData, { calendar_id: string }>(
        (params: { calendar_id: string }, options: AxiosRequestConfig) =>
          deleteCalendarConnectionApi(params.calendar_id, options),
        deleteCalendarConnectionParams,
        params,
        state.getOptions()
      )
    },

    async syncCalendar(params: {
      calendar_id: string
    }): Promise<ApiResponseV2<SyncCalendarResponseData>> {
      const { syncCalendar: syncCalendarApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { syncCalendarParams } = await import("../generated/v2/api/calendars/calendars.zod.js")

      return apiWrapperV2<SyncCalendarResponseData, { calendar_id: string }>(
        (params: { calendar_id: string }, options: AxiosRequestConfig) =>
          syncCalendarApi(params.calendar_id, options),
        syncCalendarParams,
        params,
        state.getOptions()
      )
    },

    async resubscribeCalendar(params: {
      calendar_id: string
    }): Promise<ApiResponseV2<ResubscribeCalendarResponseData>> {
      const { resubscribeCalendar: resubscribeCalendarApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { resubscribeCalendarParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<ResubscribeCalendarResponseData, { calendar_id: string }>(
        (params: { calendar_id: string }, options: AxiosRequestConfig) =>
          resubscribeCalendarApi(params.calendar_id, options),
        resubscribeCalendarParams,
        params,
        state.getOptions()
      )
    },

    async listEvents(params: {
      calendar_id: string
      query?: ListEventsParamsV2
    }): Promise<ListApiResponseV2<ListEventsResponseDataItem>> {
      const { listEvents: listEventsApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { listEventsParams: listEventsParamsSchema, listEventsQueryParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2List<
        ListEventsResponseDataItem,
        { calendar_id: string; query?: ListEventsParamsV2 }
      >(
        (params: { calendar_id: string; query?: ListEventsParamsV2 }, options) =>
          listEventsApi(
            params.calendar_id,
            params.query ?? { limit: 50, show_cancelled: true },
            options
          ),
        z.object({
          calendar_id: listEventsParamsSchema.shape.calendar_id,
          query: listEventsQueryParams.optional()
        }),
        params,
        state.getOptions()
      )
    },

    async listEventSeries(params: {
      calendar_id: string
      query?: ListEventSeriesParams
    }): Promise<ListApiResponseV2<ListEventSeriesResponseDataItem>> {
      const { listEventSeries: listEventSeriesApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { listEventSeriesParams: listEventSeriesParamsSchema, listEventSeriesQueryParams } =
        await import("../generated/v2/api/calendars/calendars.zod.js")

      return apiWrapperV2List<
        ListEventSeriesResponseDataItem,
        { calendar_id: string; query?: ListEventSeriesParams }
      >(
        (params: { calendar_id: string; query?: ListEventSeriesParams }, options) =>
          listEventSeriesApi(
            params.calendar_id,
            params.query ?? { limit: 50, show_cancelled: true },
            options
          ),
        z.object({
          calendar_id: listEventSeriesParamsSchema.shape.calendar_id,
          query: listEventSeriesQueryParams.optional()
        }),
        params,
        state.getOptions()
      )
    },

    async getEventDetails(params: {
      calendar_id: string
      event_id: string
    }): Promise<ApiResponseV2<GetEventDetailsResponseData>> {
      const { getEventDetails: getEventDetailsApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { getEventDetailsParams } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<GetEventDetailsResponseData, { calendar_id: string; event_id: string }>(
        (params: { calendar_id: string; event_id: string }, options) =>
          getEventDetailsApi(params.calendar_id, params.event_id, options),
        getEventDetailsParams,
        params,
        state.getOptions()
      )
    },

    async createCalendarBot(params: {
      calendar_id: string
      body: CreateCalendarBotRequestBodyInput
    }): Promise<
      BatchApiResponseV2<CreateCalendarBotResponseDataItem, CreateCalendarBotResponseErrorsItem>
    > {
      const { createCalendarBot: createCalendarBotApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { createCalendarBotParams, createCalendarBotBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      return apiWrapperV2<
        CreateCalendarBotResponseDataItem[],
        { calendar_id: string; body: CreateCalendarBotRequestBodyInput }
      >(
        (params: { calendar_id: string; body: CreateCalendarBotRequestBodyInput }, options) =>
          createCalendarBotApi(params.calendar_id, params.body, options),
        z.object({
          calendar_id: createCalendarBotParams.shape.calendar_id,
          body: createCalendarBotBody
        }) as z.ZodType<{ calendar_id: string; body: CreateCalendarBotRequestBodyInput }>,
        params,
        state.getOptions()
      ) as Promise<
        BatchApiResponseV2<CreateCalendarBotResponseDataItem, CreateCalendarBotResponseErrorsItem>
      >
    },

    async updateCalendarBot(params: {
      calendar_id: string
      event_id: string
      body: UpdateCalendarBotRequestBodyInput
    }): Promise<
      BatchApiResponseV2<UpdateCalendarBotResponseDataItem, UpdateCalendarBotResponseErrorsItem>
    > {
      const { updateCalendarBot: updateCalendarBotApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { updateCalendarBotParams, updateCalendarBotBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      // event_id is in the body, not the path
      const bodyWithEventId = {
        ...params.body,
        event_id: params.event_id
      } as UpdateCalendarBotRequestBodyInput

      return apiWrapperV2<
        UpdateCalendarBotResponseDataItem[],
        { calendar_id: string; body: UpdateCalendarBotRequestBodyInput }
      >(
        (params: { calendar_id: string; body: UpdateCalendarBotRequestBodyInput }, options) =>
          updateCalendarBotApi(params.calendar_id, params.body, options),
        z.object({
          calendar_id: updateCalendarBotParams.shape.calendar_id,
          body: updateCalendarBotBody
        }) as z.ZodType<{ calendar_id: string; body: UpdateCalendarBotRequestBodyInput }>,
        { calendar_id: params.calendar_id, body: bodyWithEventId },
        state.getOptions()
      ) as Promise<
        BatchApiResponseV2<UpdateCalendarBotResponseDataItem, UpdateCalendarBotResponseErrorsItem>
      >
    },

    async deleteCalendarBot(params: {
      calendar_id: string
      event_id: string
    }): Promise<
      BatchApiResponseV2<DeleteCalendarBotResponseDataItem, DeleteCalendarBotResponseErrorsItem>
    > {
      const { deleteCalendarBot: deleteCalendarBotApi } = await import(
        "../generated/v2/api/calendars/calendars.js"
      )
      const { deleteCalendarBotParams, deleteCalendarBotBody } = await import(
        "../generated/v2/api/calendars/calendars.zod.js"
      )

      // event_id is in the body, not the path. The body requires series_id, so we'll use a placeholder
      // and include event_id. This is a workaround for the interface mismatch.
      // The interface says event_id is a path param, but the API expects it in the body.
      // We construct a valid body with event_id and required fields.
      const body: DeleteCalendarBotBody = {
        event_id: params.event_id,
        all_occurrences: false,
        series_id: "00000000-0000-0000-0000-000000000000" // Placeholder UUID since series_id is required
      }

      return apiWrapperV2<
        DeleteCalendarBotResponseDataItem[],
        { calendar_id: string; body: DeleteCalendarBotBody }
      >(
        (params: { calendar_id: string; body: DeleteCalendarBotBody }, options) =>
          deleteCalendarBotApi(params.calendar_id, params.body, options),
        z.object({
          calendar_id: deleteCalendarBotParams.shape.calendar_id,
          body: deleteCalendarBotBody
        }),
        { calendar_id: params.calendar_id, body },
        state.getOptions()
      ) as Promise<
        BatchApiResponseV2<DeleteCalendarBotResponseDataItem, DeleteCalendarBotResponseErrorsItem>
      >
    },

    // Meet workspace methods
    async createMeetWorkspace(
      params: CreateMeetWorkspaceBody
    ): Promise<ApiResponseV2<CreateMeetWorkspace201Data>> {
      const { createMeetWorkspace: createMeetWorkspaceApi } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.js"
      )
      const { createMeetWorkspaceBody } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.zod.js"
      )

      return apiWrapperV2<CreateMeetWorkspace201Data, CreateMeetWorkspaceBody>(
        createMeetWorkspaceApi,
        createMeetWorkspaceBody as z.ZodType<CreateMeetWorkspaceBody>,
        params,
        state.getOptions()
      )
    },

    async listMeetWorkspaces(
      params?: ListMeetWorkspacesParams
    ): Promise<ApiResponseV2<ListMeetWorkspaces200DataItem[]>> {
      const { listMeetWorkspaces: listMeetWorkspacesApi } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.js"
      )

      return apiWrapperV2NoParams(
        (options: AxiosRequestConfig) => listMeetWorkspacesApi(params, options),
        state.getOptions()
      )
    },

    async getMeetWorkspace(params: {
      workspace_id: string
    }): Promise<ApiResponseV2<GetMeetWorkspace200Data>> {
      const { getMeetWorkspace: getMeetWorkspaceApi } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.js"
      )
      const { getMeetWorkspaceParams } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.zod.js"
      )

      return apiWrapperV2<GetMeetWorkspace200Data, { workspace_id: string }>(
        (params: { workspace_id: string }, options: AxiosRequestConfig) =>
          getMeetWorkspaceApi(params.workspace_id, options),
        getMeetWorkspaceParams,
        params,
        state.getOptions()
      )
    },

    async updateMeetWorkspace(params: {
      workspace_id: string
      body: UpdateMeetWorkspaceBody
    }): Promise<ApiResponseV2<UpdateMeetWorkspace200Data>> {
      const { updateMeetWorkspace: updateMeetWorkspaceApi } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.js"
      )
      const { updateMeetWorkspaceParams, updateMeetWorkspaceBody } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.zod.js"
      )

      return apiWrapperV2<
        UpdateMeetWorkspace200Data,
        { workspace_id: string; body: UpdateMeetWorkspaceBody }
      >(
        (params: { workspace_id: string; body: UpdateMeetWorkspaceBody }, options) =>
          updateMeetWorkspaceApi(params.workspace_id, params.body, options),
        z.object({
          workspace_id: updateMeetWorkspaceParams.shape.workspace_id,
          body: updateMeetWorkspaceBody
        }) as z.ZodType<{ workspace_id: string; body: UpdateMeetWorkspaceBody }>,
        params,
        state.getOptions()
      )
    },

    async deleteMeetWorkspace(params: {
      workspace_id: string
    }): Promise<ApiResponseV2<DeleteMeetWorkspace200Data>> {
      const { deleteMeetWorkspace: deleteMeetWorkspaceApi } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.js"
      )
      const { deleteMeetWorkspaceParams } = await import(
        "../generated/v2/api/meet-workspaces/meet-workspaces.zod.js"
      )

      return apiWrapperV2<DeleteMeetWorkspace200Data, { workspace_id: string }>(
        (params: { workspace_id: string }, options: AxiosRequestConfig) =>
          deleteMeetWorkspaceApi(params.workspace_id, options),
        deleteMeetWorkspaceParams,
        params,
        state.getOptions()
      )
    },

    // Meet login methods
    async createMeetLogin(
      params: CreateMeetLoginBody
    ): Promise<ApiResponseV2<CreateMeetLogin201Data>> {
      const { createMeetLogin: createMeetLoginApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )
      const { createMeetLoginBody } = await import(
        "../generated/v2/api/meet-logins/meet-logins.zod.js"
      )

      return apiWrapperV2<CreateMeetLogin201Data, CreateMeetLoginBody>(
        createMeetLoginApi,
        createMeetLoginBody as z.ZodType<CreateMeetLoginBody>,
        params,
        state.getOptions()
      )
    },

    async listMeetLogins(
      params?: ListMeetLoginsParams
    ): Promise<ApiResponseV2<ListMeetLogins200DataItem[]>> {
      const { listMeetLogins: listMeetLoginsApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )

      return apiWrapperV2NoParams(
        (options: AxiosRequestConfig) => listMeetLoginsApi(params, options),
        state.getOptions()
      )
    },

    async getMeetLoginUtilization(): Promise<ApiResponseV2<GetMeetLoginUtilization200Data>> {
      const { getMeetLoginUtilization: getMeetLoginUtilizationApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )

      return apiWrapperV2NoParams(getMeetLoginUtilizationApi, state.getOptions())
    },

    async getMeetLogin(params: {
      credential_id: string
    }): Promise<ApiResponseV2<GetMeetLogin200Data>> {
      const { getMeetLogin: getMeetLoginApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )
      const { getMeetLoginParams } = await import(
        "../generated/v2/api/meet-logins/meet-logins.zod.js"
      )

      return apiWrapperV2<GetMeetLogin200Data, { credential_id: string }>(
        (params: { credential_id: string }, options: AxiosRequestConfig) =>
          getMeetLoginApi(params.credential_id, options),
        getMeetLoginParams,
        params,
        state.getOptions()
      )
    },

    async updateMeetLogin(params: {
      credential_id: string
      body: UpdateMeetLoginBody
    }): Promise<ApiResponseV2<UpdateMeetLogin200Data>> {
      const { updateMeetLogin: updateMeetLoginApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )
      const { updateMeetLoginParams, updateMeetLoginBody } = await import(
        "../generated/v2/api/meet-logins/meet-logins.zod.js"
      )

      return apiWrapperV2<
        UpdateMeetLogin200Data,
        { credential_id: string; body: UpdateMeetLoginBody }
      >(
        (params: { credential_id: string; body: UpdateMeetLoginBody }, options) =>
          updateMeetLoginApi(params.credential_id, params.body, options),
        z.object({
          credential_id: updateMeetLoginParams.shape.credential_id,
          body: updateMeetLoginBody
        }) as z.ZodType<{ credential_id: string; body: UpdateMeetLoginBody }>,
        params,
        state.getOptions()
      )
    },

    async deleteMeetLogin(params: {
      credential_id: string
    }): Promise<ApiResponseV2<DeleteMeetLogin200Data>> {
      const { deleteMeetLogin: deleteMeetLoginApi } = await import(
        "../generated/v2/api/meet-logins/meet-logins.js"
      )
      const { deleteMeetLoginParams } = await import(
        "../generated/v2/api/meet-logins/meet-logins.zod.js"
      )

      return apiWrapperV2<DeleteMeetLogin200Data, { credential_id: string }>(
        (params: { credential_id: string }, options: AxiosRequestConfig) =>
          deleteMeetLoginApi(params.credential_id, options),
        deleteMeetLoginParams,
        params,
        state.getOptions()
      )
    },

    // Zoom credentials methods
    async createZoomCredential(
      params: CreateZoomCredentialBody
    ): Promise<ApiResponseV2<CreateZoomCredential201Data>> {
      const { createZoomCredential: createZoomCredentialApi } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.js"
      )
      const { createZoomCredentialBody } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.zod.js"
      )

      return apiWrapperV2<CreateZoomCredential201Data, CreateZoomCredentialBody>(
        createZoomCredentialApi,
        createZoomCredentialBody as z.ZodType<CreateZoomCredentialBody>,
        params,
        state.getOptions()
      )
    },

    async listZoomCredentials(
      params?: ListZoomCredentialsParams
    ): Promise<ApiResponseV2<ListZoomCredentials200DataItem[]>> {
      const { listZoomCredentials: listZoomCredentialsApi } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.js"
      )

      return apiWrapperV2NoParams(
        (options: AxiosRequestConfig) => listZoomCredentialsApi(params, options),
        state.getOptions()
      )
    },

    async getZoomCredential(params: {
      id: string
    }): Promise<ApiResponseV2<GetZoomCredential200Data>> {
      const { getZoomCredential: getZoomCredentialApi } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.js"
      )
      const { getZoomCredentialParams } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.zod.js"
      )

      return apiWrapperV2<GetZoomCredential200Data, { id: string }>(
        (params: { id: string }, options: AxiosRequestConfig) =>
          getZoomCredentialApi(params.id, options),
        getZoomCredentialParams,
        params,
        state.getOptions()
      )
    },

    async updateZoomCredential(params: {
      id: string
      body: UpdateZoomCredentialBody
    }): Promise<ApiResponseV2<UpdateZoomCredential200Data>> {
      const { updateZoomCredential: updateZoomCredentialApi } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.js"
      )
      const { updateZoomCredentialParams, updateZoomCredentialBody } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.zod.js"
      )

      return apiWrapperV2<
        UpdateZoomCredential200Data,
        { id: string; body: UpdateZoomCredentialBody }
      >(
        (params: { id: string; body: UpdateZoomCredentialBody }, options) =>
          updateZoomCredentialApi(params.id, params.body, options),
        z.object({
          id: updateZoomCredentialParams.shape.id,
          body: updateZoomCredentialBody
        }),
        params,
        state.getOptions()
      )
    },

    async deleteZoomCredential(params: {
      id: string
    }): Promise<ApiResponseV2<DeleteZoomCredential200Data>> {
      const { deleteZoomCredential: deleteZoomCredentialApi } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.js"
      )
      const { deleteZoomCredentialParams } = await import(
        "../generated/v2/api/zoom-credentials/zoom-credentials.zod.js"
      )

      return apiWrapperV2<DeleteZoomCredential200Data, { id: string }>(
        (params: { id: string }, options: AxiosRequestConfig) =>
          deleteZoomCredentialApi(params.id, options),
        deleteZoomCredentialParams,
        params,
        state.getOptions()
      )
    },

    getApiKey(): string {
      return state.getApiKey()
    },

    getBaseUrl(): string {
      return state.getBaseUrl()
    }
  }
}
